/**
 * Import outcomes per file, as tables shared by the import pages.
 *
 * @module
 */
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import type { ImportItem } from "../api/client";

export const OUTCOME: Record<
  string,
  { label: string; color: "success" | "warning" | "default" | "error" }
> = {
  catalogued: { label: "Imported", color: "success" },
  needs_review: { label: "Needs review", color: "warning" },
  duplicate: { label: "Already held", color: "default" },
  rejected: { label: "Rejected", color: "error" },
};

export const TIME_FROM: Record<string, string> = {
  EmbeddedMetadata: "Embedded metadata",
  Filename: "Filename",
  FileSystemMtime: "File time",
  Manual: "Set by hand",
};

export function ItemsHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>File</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>Start (UTC)</TableCell>
        <TableCell>Time from</TableCell>
        <TableCell align="right">Size</TableCell>
        <TableCell>SHA-256</TableCell>
        <TableCell>Status</TableCell>
      </TableRow>
    </TableHead>
  );
}

export function ItemsTable({ items }: { items: ImportItem[] }) {
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{ minWidth: 860, "& .nowrap": { whiteSpace: "nowrap" } }}
      >
        <ItemsHead />
        <TableBody>
          {items.map((i, n) => (
            <ItemRow key={`${i.sha256}:${n}`} item={i} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function ItemRow({ item }: { item: ImportItem }) {
  const outcome = OUTCOME[item.outcome] ?? {
    label: item.outcome,
    color: "default" as const,
  };
  const detail =
    item.reason ?? (item.problems.length ? item.problems.join("; ") : null);
  return (
    <TableRow>
      <TableCell className="nowrap" sx={{ fontFamily: "monospace" }}>
        {item.unit ? `${item.unit}/` : ""}
        {item.original_name}
      </TableCell>
      <TableCell>{item.source ?? "Unassigned"}</TableCell>
      <TableCell className="nowrap" sx={{ fontFamily: "monospace" }}>
        {item.start_utc ? utc(item.start_utc) : "—"}
      </TableCell>
      <TableCell>
        {item.time_basis
          ? `${TIME_FROM[item.time_basis] ?? item.time_basis} (${item.time_confidence ?? "?"})`
          : "—"}
      </TableCell>
      <TableCell className="nowrap" align="right">
        {size(item.size_bytes)}
      </TableCell>
      <TableCell
        className="nowrap"
        sx={{ fontFamily: "monospace" }}
        title={item.sha256}
      >
        {item.sha256.slice(0, 12)}…
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Chip
            size="small"
            color={outcome.color}
            label={outcome.label}
            sx={{ alignSelf: "flex-start" }}
          />
          {detail && (
            <Typography variant="caption" color="text.secondary">
              {detail}
            </Typography>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function utc(iso: string): string {
  return iso
    .replace("T", " ")
    .replace(/(\.\d{3})\d*/, "$1")
    .replace(/(Z|\+00:00)$/, "");
}

export function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[u]}`;
}
